import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { Client } from '@df/core/service/client';
import { AttributeLabelPipe } from '@df/ui/common/attribute-label.pipe';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { Subscription } from 'rxjs/internal/Subscription';
import { IParentView } from '../view/layout/product-view-layout.abstract';
import {
    EProductGeneralDetailsAttributeMode,
    IProductGeneralDetailsAttributeData,
    IProductViewLayoutContent
} from '../view/layout/product-view-layout.interface';
import { ProductGeneralDetailsItemComponent } from './item/product-general-details-item.component';

interface IAttribute {
    attributes: {
        attribute: IProductGeneralDetailsAttributeData;
        mode: EProductGeneralDetailsAttributeMode;
        code: string;
        values: any[];
        valuesLabels: string;
    }[];
    bulletpoints: boolean;
    heading: string;
}

@Component({
    selector: 'product-general-details',
    templateUrl: './product-general-details.html',
    standalone: true,
    imports: [AttributeLabelPipe, CommonModule, ProductGeneralDetailsItemComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductGeneralDetailsComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true })
    parentView!: IParentView;

    @Input()
    content?: IProductViewLayoutContent;

    readonly EMode = EProductGeneralDetailsAttributeMode;
    sections: IAttribute[] = [];
    private parentViewDetectChanges?: Subscription;
    private parentViewProductChanges?: Subscription;

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected attributeResource: AttributeResource,
        protected client: Client
    ) {}

    ngOnInit() {
        this.updateData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['parentView']) {
            this.parentViewDetectChanges?.unsubscribe();

            this.parentViewDetectChanges = this.parentView.detectChanges$.subscribe(() => this.changeDetectorRef.detectChanges());

            this.parentViewProductChanges?.unsubscribe();

            this.parentViewProductChanges = this.parentView.productChange?.subscribe(() => this.updateData());
        }
    }

    ngOnDestroy() {
        this.parentViewDetectChanges?.unsubscribe();
        this.parentViewProductChanges?.unsubscribe();
    }

    protected updateData() {
        this.sections = this._getSections();

        this.changeDetectorRef.detectChanges();
    }

    private _getSections(): IAttribute[] {
        const sections = this.content?.detailsAttributes
            ?.map(item => ({
                heading: item.heading,
                bulletpoints: item.bulletpoints,
                attributes: item.attributes.map(attribute => {
                    const code = attribute.type === 'attribute' ? attribute.code : attribute.code_manual;
                    const values = this._getValues(code);
                    return {
                        attribute,
                        code,
                        mode: attribute.mode,
                        values: this.client.isCms ? values : values.filter(i => !!i),
                        valuesLabels: attribute.mode === EProductGeneralDetailsAttributeMode.cms ? '' : this.getValuesLabels(values, code)
                    };
                })
            }))
            .filter(i => !!i?.attributes?.find(attribute => !!attribute.values.find(value => !!value)));

        return sections;
    }

    protected getValuesLabels(values: (string | number)[], attributeCode: string): string {
        const attribute = this.attributeResource.getExisting(attributeCode);
        const valuesLabels = values.map(value => optionLabelPipe(value, attributeCode));
        if (!attribute?.data?.options) {
            return valuesLabels.join(',');
        } else if (valuesLabels.length) {
            return `<ul>${valuesLabels.map(i => `<li>${i}</li>`).join('')}</ul>`;
        } else {
            return '';
        }
    }

    /**
     * Gets value
     */
    private _getValues(code: string): (string | number)[] {
        const value = this.parentView.product.data[code] || this.parentView.parent.data[code];

        if (value) {
            return Array.isArray(value) ? value : [value];
        }

        if (this.parentView.product.isBundle) {
            return (
                uniq(
                    flatten(
                        this.parentView.product
                            .getBundleSelectedProducts(this.parentView.productView.bundleSelection)
                            ?.map(i => i.product.data[code]) || []
                    )
                ).filter(i => typeof i !== 'undefined') || []
            );
        }

        return [];
    }
}
